import { useState, useEffect, useRef, useCallback } from 'react'
import {
  QrCode,
  Search,
  History,
  Camera,
  CameraOff,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  ScanLine,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { validateQrCode, scanQrCode, getReservationByManualLookup } from '../services/qrCodeService'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReservationDetails {
  reservation_id: string
  user_id: string
  merchant_id: string
  status: string
  pickup_time: string | null
  scheduled_at: string | null
  total_amount: number | string
  quantity: number
  item_name?: string | null
  merchant_name?: string | null
  user_first_name?: string | null
  user_last_name?: string | null
  created_at?: string
  qr_code_expires_at?: string | null
}

interface ScanHistoryEntry {
  id: string
  reservationId: string
  timestamp: string
  status: 'success' | 'error'
  details?: string
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

type TabId = 'scanner' | 'manual' | 'history'

interface Tab {
  id: TabId
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const TABS: Tab[] = [
  { id: 'scanner', label: 'Scanner', icon: ScanLine },
  { id: 'manual', label: 'Recherche manuelle', icon: Search },
  { id: 'history', label: 'Historique', icon: History },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const QrScannerPage = () => {
  // Active tab
  const [activeTab, setActiveTab] = useState<TabId>('scanner')

  // Camera scanner state
  const [cameraActive, setCameraActive] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const html5QrCodeRef = useRef<any>(null)

  // Scanned / looked-up reservation
  const [reservation, setReservation] = useState<ReservationDetails | null>(null)
  const [loadingReservation, setLoadingReservation] = useState(false)
  const [confirmingPickup, setConfirmingPickup] = useState(false)

  // Manual lookup
  const [manualId, setManualId] = useState('')

  // Scan history
  const [scanHistory, setScanHistory] = useState<ScanHistoryEntry[]>([])

  // -----------------------------------------------------------------------
  // Cleanup camera on unmount
  // -----------------------------------------------------------------------
  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [])

  // -----------------------------------------------------------------------
  // Camera helpers
  // -----------------------------------------------------------------------

  const stopCamera = useCallback(() => {
    setCameraActive(false)
    if (html5QrCodeRef.current) {
      try {
        html5QrCodeRef.current.stop()
      } catch {
        // ignore
      }
      html5QrCodeRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  const startCamera = useCallback(async () => {
    setCameraError(null)
    setScanning(true)

    try {
      // Dynamically import html5-qrcode
      const { Html5Qrcode } = await import('html5-qrcode')

      // Check if camera permissions are granted
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach((track) => track.stop())
      } catch {
        setCameraError(
          'Camera permission denied. Please allow camera access in your browser settings and try again.'
        )
        setScanning(false)
        return
      }

      const html5QrCode = new Html5Qrcode('qr-reader')
      html5QrCodeRef.current = html5QrCode

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText: string) => {
          // On successful decode
          handleQrCodeScanned(decodedText)
        },
        () => {
          // Ignore intermediate frames
        }
      )

      setCameraActive(true)
      setScanning(false)
    } catch (err: any) {
      console.error('Camera start error:', err)
      setCameraError(
        err?.message || "Impossible de démarrer la caméra. Assurez-vous d'utiliser HTTPS ou localhost."
      )
      setScanning(false)
    }
  }, [])

  // -----------------------------------------------------------------------
  // QR code scanned handler
  // -----------------------------------------------------------------------

  const handleQrCodeScanned = useCallback(
    async (decodedText: string) => {
      if (confirmingPickup) return // prevent double-scan

      // Stop camera after successful scan
      stopCamera()

      // Add to history
      const historyEntry: ScanHistoryEntry = {
        id: Date.now().toString(),
        reservationId: decodedText.slice(0, 36), // first 36 chars as ID
        timestamp: new Date().toISOString(),
        status: 'success',
        details: 'QR Code scanned',
      }

      setLoadingReservation(true)

      try {
        // First validate the QR code
        const validation = await validateQrCode(decodedText)

        if (!validation.valid) {
          toast.error(validation.error || 'Invalid QR code')
          historyEntry.status = 'error'
          historyEntry.details = validation.error || 'Invalid QR code'
          setScanHistory((prev) => [historyEntry, ...prev])
          setLoadingReservation(false)
          return
        }

        // If valid, set the reservation details
        if (validation.data) {
          setReservation(validation.data)
          toast.success('QR Code validé avec succès')
        }

        setScanHistory((prev) => [historyEntry, ...prev])
      } catch (err: any) {
        toast.error(err?.message || 'Échec de la validation du code QR')
        historyEntry.status = 'error'
        historyEntry.details = err?.message || 'Échec de la validation'
        setScanHistory((prev) => [historyEntry, ...prev])
      } finally {
        setLoadingReservation(false)
      }
    },
    [confirmingPickup, stopCamera]
  )

  // -----------------------------------------------------------------------
  // Manual lookup
  // -----------------------------------------------------------------------

  const handleManualLookup = useCallback(async () => {
    if (!manualId.trim()) {
      toast.error('Veuillez entrer un ID de réservation')
      return
    }

    setLoadingReservation(true)
    setReservation(null)

    try {
      const result = await getReservationByManualLookup(manualId.trim())

      if (result.success && result.data) {
        setReservation(result.data)
        toast.success('Réservation trouvée')

        setScanHistory((prev) => [
          {
            id: Date.now().toString(),
            reservationId: manualId.trim(),
            timestamp: new Date().toISOString(),
            status: 'success',
            details: 'Recherche manuelle',
          },
          ...prev,
        ])
      } else {
        toast.error(result.error || 'Réservation non trouvée')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la recherche')
    } finally {
      setLoadingReservation(false)
    }
  }, [manualId])

  // -----------------------------------------------------------------------
  // Confirm pickup
  // -----------------------------------------------------------------------

  const handleConfirmPickup = useCallback(async () => {
    if (!reservation) return

    setConfirmingPickup(true)

    try {
      const result = await scanQrCode(reservation.reservation_id)

      if (result.success) {
        toast.success('Retrait confirmé avec succès !')
        setReservation((prev) =>
          prev ? { ...prev, status: 'completed' } : prev
        )
      } else {
        toast.error(result.error || 'Erreur lors de la confirmation du retrait')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la confirmation')
    } finally {
      setConfirmingPickup(false)
    }
  }, [reservation])

  // -----------------------------------------------------------------------
  // Reset
  // -----------------------------------------------------------------------

  const handleReset = useCallback(() => {
    setReservation(null)
    setManualId('')
    setCameraError(null)
  }, [])

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const renderStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-purple-100 text-purple-800',
      ready: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-600',
    }

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          colors[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {status}
      </span>
    )
  }

  const renderReservationDetails = () => {
    if (!reservation) return null

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Détails de la réservation
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">ID Réservation</span>
            <p className="text-sm font-mono font-medium text-gray-900 break-all">
              {reservation.reservation_id}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Statut</span>
            <div className="mt-1">{renderStatusBadge(reservation.status)}</div>
          </div>
          {reservation.merchant_name && (
            <div>
              <span className="text-sm text-gray-500">Commerçant</span>
              <p className="text-sm font-medium text-gray-900">
                {reservation.merchant_name}
              </p>
            </div>
          )}
          {reservation.item_name && (
            <div>
              <span className="text-sm text-gray-500">Article</span>
              <p className="text-sm font-medium text-gray-900">
                {reservation.item_name}
              </p>
            </div>
          )}
          {reservation.user_first_name && (
            <div>
              <span className="text-sm text-gray-500">Client</span>
              <p className="text-sm font-medium text-gray-900">
                {reservation.user_first_name} {reservation.user_last_name || ''}
              </p>
            </div>
          )}
          <div>
            <span className="text-sm text-gray-500">Quantité</span>
            <p className="text-sm font-medium text-gray-900">
              {reservation.quantity}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Montant total</span>
            <p className="text-sm font-medium text-gray-900">
              {typeof reservation.total_amount === 'number'
                ? reservation.total_amount.toFixed(2)
                : reservation.total_amount}{' '}
              €
            </p>
          </div>
          {reservation.pickup_time && (
            <div>
              <span className="text-sm text-gray-500">Heure de retrait</span>
              <p className="text-sm font-medium text-gray-900">
                {new Date(reservation.pickup_time).toLocaleString()}
              </p>
            </div>
          )}
          {reservation.scheduled_at && (
            <div>
              <span className="text-sm text-gray-500">Planifié le</span>
              <p className="text-sm font-medium text-gray-900">
                {new Date(reservation.scheduled_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Confirm Pickup Button */}
        {reservation.status !== 'completed' && reservation.status !== 'cancelled' && (
          <div className="mt-6 flex gap-3">
            <button
              onClick={handleConfirmPickup}
              disabled={confirmingPickup}
              className="inline-flex items-center px-6 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {confirmingPickup ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Valider le retrait
            </button>
            <button
              onClick={handleReset}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Annuler
            </button>
          </div>
        )}

        {/* Already completed badge */}
        {reservation.status === 'completed' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-800">
              Cette réservation a déjà été retirée
            </span>
          </div>
        )}

        {reservation.status === 'cancelled' && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm font-medium text-red-800">
              Cette réservation a été annulée
            </span>
          </div>
        )}
      </div>
    )
  }

  const renderScannerTab = () => (
    <div>
      {/* Camera area */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Scanner un QR Code
          </h3>
          {cameraActive && (
            <button
              onClick={stopCamera}
              className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
            >
              <CameraOff className="w-4 h-4 mr-1.5" />
              Arrêter
            </button>
          )}
        </div>

        {/* QR Reader container */}
        <div
          id="qr-reader"
          className={`w-full max-w-md mx-auto overflow-hidden rounded-lg ${
            cameraActive ? '' : 'hidden'
          }`}
        />

        {/* Camera placeholder / start button */}
        {!cameraActive && !scanning && (
          <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <Camera className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-500 mb-4 text-center">
              Cliquez ci-dessous pour activer la caméra et scanner un QR Code
            </p>
            <button
              onClick={startCamera}
              className="inline-flex items-center px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Camera className="w-4 h-4 mr-2" />
              Activer la caméra
            </button>
          </div>
        )}

        {/* Scanning state */}
        {scanning && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-500">Initialisation de la caméra...</p>
          </div>
        )}

        {/* Camera error */}
        {cameraError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Erreur de caméra
                </p>
                <p className="text-sm text-red-700 mt-1">{cameraError}</p>
                <button
                  onClick={() => setCameraError(null)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HTTPS warning */}
        {!window.location.protocol.includes('https') &&
          !window.location.hostname.includes('localhost') && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  L'accès à la caméra nécessite une connexion HTTPS sécurisée.
                  En développement local (localhost), cela fonctionne sans HTTPS.
                </p>
              </div>
            </div>
          )}
      </div>

      {/* Reservation details after scan */}
      {loadingReservation && (
        <div className="flex items-center justify-center py-8 mt-6">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-500">Validation du QR Code...</span>
        </div>
      )}

      {!loadingReservation && renderReservationDetails()}
    </div>
  )

  const renderManualTab = () => (
    <div>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recherche manuelle
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Entrez l'ID de réservation pour rechercher manuellement une
          réservation.
        </p>

        <div className="flex gap-3">
          <input
            type="text"
            value={manualId}
            onChange={(e) => setManualId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleManualLookup()
            }}
            placeholder="Entrez l'ID de réservation..."
            className="flex-1 rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500 px-4 py-2 border text-sm"
          />
          <button
            onClick={handleManualLookup}
            disabled={loadingReservation || !manualId.trim()}
            className="inline-flex items-center px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loadingReservation ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            Rechercher
          </button>
        </div>
      </div>

      {loadingReservation && (
        <div className="flex items-center justify-center py-8 mt-6">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-500">Recherche en cours...</span>
        </div>
      )}

      {!loadingReservation && renderReservationDetails()}
    </div>
  )

  const renderHistoryTab = () => (
    <div>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Historique des scans
          </h3>
          {scanHistory.length > 0 && (
            <button
              onClick={() => setScanHistory([])}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Effacer l'historique
            </button>
          )}
        </div>

        {scanHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <History className="w-12 h-12 mb-3" />
            <p className="text-sm">Aucun scan pour le moment</p>
            <p className="text-xs mt-1">
              Les QR Codes scannés apparaîtront ici
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date / Heure
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID Réservation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Détails
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scanHistory.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                      {entry.reservationId.slice(0, 12)}...
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {entry.status === 'success' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Succès
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="w-3 h-3 mr-1" />
                          Erreur
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {entry.details || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <QrCode className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Scanner QR Code
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Scannez les QR Codes des réservations pour valider les retraits
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8" aria-label="Onglets">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id)
                  if (tab.id !== 'scanner') {
                    stopCamera()
                  }
                }}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'scanner' && renderScannerTab()}
      {activeTab === 'manual' && renderManualTab()}
      {activeTab === 'history' && renderHistoryTab()}
    </div>
  )
}

export default QrScannerPage
